import React, { useEffect, useState, useRef } from "react";
import { Form, Input, Space, Button, Modal, notification } from "antd";
import type { TableColumnsType, TableProps, FormInstance, InputRef, TableColumnType } from "antd";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { ExclamationCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { Progress } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import { useLocation } from "react-router-dom";

function StartLicense(stimulsoftInstance: any) {
    const key = process.env.REACT_APP_STIMULSOFT_LICENSE_KEY;
    if (key && key.trim() !== "") {
        try {
            stimulsoftInstance.Base.StiLicense.key = key.trim();
        } catch (error: any) { }
    } else {
        const hardcodedKey = "6vJhGtLLLz2GNviWmUTrhSqnOItdDwjBylQzQcAOiHmKmIYtQbGVffUafn4grxXukjNz+mLI/d2lO8A9DuHxAxu4MmrN4lqhtklB0Pcnfq+AIk+1s6kU0D6JIIxpfQs1FV6pzwrzAWDLexRPvVgpswR/C5n69gFjFmWmtV0bGCTAj6H/MHgwElWYT9nYccmuFH0sfAj7VELa2HehqREJ7di+05AWeWIScfcWsf0XZ3JK+2X3reYvhUEAxrCwX3FDw2p1hPQIfiUVkUd4NRvfwWBLOjP2ZTfp9dzNe1D6T7miK1VBd731o/ex6q1bkdNH+G1KV6OLxdQwb9zWr4AR0QtMKwrphskgAYb5PMCectoVCeRFKtWSm8qnMP+GN8v0GHbJGQ/ZYWMAsE94nCEhnJz9T/xfy1b6WEWqpUUjVsfPzm/naRz0bxVN+AtFW7thTBMwGXoKTJXnKhfrI5u/fRffUu78ejnazBjWCHANN/xF7XOLhnFWw5JZHPXkaoCbiG8kBL3bgL4DtSyZ7jecAPmJUtainwLNMdo75J6hvu/pS+jDUs2tqRjEv6PTehXg";
        try {
            stimulsoftInstance.Base.StiLicense.key = hardcodedKey;
        } catch (fallbackError: any) { }
    }
}

export interface DataType {
    key: React.Key;
    item_number: string;
    item_name: string;
    uom: string;
    qty_available: number;
    qty_request: number;
    qty_out: number;
    qty_in: string;
    location: string;
}

type DataIndex = keyof DataType;
export interface WithItemWHTransferInLogicProps {
    columns: TableColumnsType<DataType>;
    rowSelection: TableProps<DataType>["rowSelection"];
    transferInDate: string;
    form: FormInstance;
    isModalOpen: boolean;
    showModal: () => void;
    handleOk: () => void;
    handleCancel: () => void;
    dataSource: DataType[];
    handleDataSelect: (record: any) => void;
    isEditModalOpen: boolean;
    editingRecord: DataType | null;
    handleRowDoubleClick: (record: DataType) => void;
    handleEditOk: (values: any) => void;
    handleEditCancel: () => void;
    autosaveStatus: 'idle' | 'processing' | 'error' | 'stopped';
    onValuesChange: () => void;
    handleSubmit: () => void;
    isSubmitting: boolean;
    isSubmitted: boolean;
    handlePrint: () => void
    isPrinting: boolean;
    isReportModalOpen: boolean;
    handleReportModalCancel: () => void;
}

const withItemWHTransferInLogic = <P extends object>(
    WrappedComponent: React.ComponentType<P & WithItemWHTransferInLogicProps>
) => {
    const EnhancedComponent: React.FC<P> = (props) => {
        const [form] = Form.useForm();
        const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
        const [transferInDate, setTransferInDate] = useState<string>("");
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [dataSource, setDataSource] = useState<DataType[]>([]);
        const { userData } = useAuth();
        const [isEditModalOpen, setIsEditModalOpen] = useState(false);
        const [editingRecord, setEditingRecord] = useState<DataType | null>(null);
        const [searchText, setSearchText] = useState('');
        const [searchedColumn, setSearchedColumn] = useState('');
        const searchInput = useRef<InputRef>(null);
        const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'processing' | 'error' | 'stopped'>('stopped');
        const autosaveTimer = useRef<NodeJS.Timeout | null>(null);
        const dataSourceRef = useRef(dataSource);
        const location = useLocation();
        const trans_in_no = location.state?.trans_in_no;
        const [creatorCode, setCreatorCode] = useState<string | null>(null);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [isSubmitted, setIsSubmitted] = useState(false);
        const [isPrinting, setIsPrinting] = useState(false);
        const [isReportModalOpen, setIsReportModalOpen] = useState(false);

        useEffect(() => {
            dataSourceRef.current = dataSource;
        }, [dataSource]);

        const saveData = async () => {
            const currentTransInNo = form.getFieldValue('trans_in_no');
            if (autosaveStatus === 'stopped' || !currentTransInNo) {
                if (autosaveStatus !== 'stopped') setAutosaveStatus('idle');
                return;
            }

            try {
                const formValues = form.getFieldsValue(true);

                const parseDate = (dateStr: string | undefined): string | null => {
                    if (!dateStr || typeof dateStr !== 'string') return null;
                    const parts = dateStr.split('/');
                    if (parts.length === 3) {
                        return `${parts[2]}-${parts[1]}-${parts[0]}`;
                    }
                    return null;
                };

                const headerData = {
                    created: creatorCode || userData?.kode_grup1,
                    status: 1,
                    trans_in_no: formValues.trans_in_no,
                    trans_in_date: parseDate(formValues.trans_in_date),
                    trans_out_no: formValues.trans_out_no,
                    trans_out_date: parseDate(formValues.trans_out_date),
                    trans_request_no: formValues.trans_request_no,
                    trans_request_date: parseDate(formValues.trans_request_date),
                    warehouse_name_from: formValues.warehouse_name_from,
                    warehouse_code_from: formValues.warehouse_code_from,
                    warehouse_name_to: formValues.warehouse_name_to,
                    warehouse_code_to: formValues.warehouse_code_to,
                    purpose: formValues.purpose,
                    approval: formValues.approval,
                    approval_date: parseDate(formValues.approval_date),
                    approval_remark: formValues.approval_remark,
                };

                const itemsData = dataSourceRef.current.map(item => ({
                    trans_in_no: formValues.trans_in_no,
                    item_number: item.item_number,
                    item_name: item.item_name,
                    uom: item.uom,
                    location: item.location,
                    qty_available: item.qty_available,
                    qty_request: item.qty_request,
                    qty_out: item.qty_out,
                    qty_in: item.qty_in,
                }));

                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/save-transfer-in`, {
                    header: headerData,
                    items: itemsData,
                });

                setAutosaveStatus('idle');
            } catch (error) {
                console.error("Autosave failed:", error);
                setAutosaveStatus('error');
            }
        };

        const triggerAutosave = () => {
            if (autosaveStatus === 'stopped') return;
            setAutosaveStatus('processing');
            if (autosaveTimer.current) {
                clearTimeout(autosaveTimer.current);
            }
            autosaveTimer.current = setTimeout(() => {
                saveData();
            }, 2000);
        };

        const showModal = () => {
            setIsModalOpen(true);
        };

        const handleOk = () => {
            setIsModalOpen(false);
        };

        const handleCancel = () => {
            setIsModalOpen(false);
        };

        const handleRowDoubleClick = (record: DataType) => {
            if (isSubmitted) return;
            setEditingRecord(record);
            setIsEditModalOpen(true);
        };

        const handleEditOk = (values: any) => {
            if (isSubmitted) return;
            const updatedDataSource = dataSource.map((item) => {
                if (item.key === editingRecord?.key) {
                    return { ...item, ...values };
                }
                return item;
            });
            setDataSource(updatedDataSource);
            setIsEditModalOpen(false);
            setEditingRecord(null);
            triggerAutosave();
        };

        const handleEditCancel = () => {
            setIsEditModalOpen(false);
            setEditingRecord(null);
        };

        const handleSubmit = () => {
            if (isSubmitting || isSubmitted) return;
            const currentStatus = form.getFieldValue('status');
            if (currentStatus && currentStatus !== 'Draft') {
                Modal.warning({ title: 'Sudah Disubmit', content: 'Transfer In ini sudah disubmit sebelumnya.' });
                return;
            }
            const items = dataSourceRef.current;
            if (!items || items.length === 0) {
                Modal.warning({
                    title: 'Tidak Bisa Submit',
                    content: 'Tidak ada item untuk di-submit.',
                });
                return;
            }

            const incompleteItem = items.find(item => !item.location || !item.qty_in || Number(item.qty_in) <= 0);
            if (incompleteItem) {
                Modal.error({
                    title: 'Data Tidak Lengkap',
                    content: `Pastikan semua item memiliki 'In Qty' (> 0) dan 'Location' yang valid sebelum submit. Cek item: ${incompleteItem.item_number}.`,
                });
                return;
            }

            Modal.confirm({
                title: 'Apakah Anda yakin ingin men-submit transfer ini?',
                icon: <ExclamationCircleOutlined />,
                content: 'Tindakan ini akan memperbarui jumlah stok dan tidak dapat dibatalkan.',
                okText: 'Ya',
                okType: 'primary',
                cancelText: 'Tidak',
                onOk: handleConfirmSubmit,
            });
        };

        const handleConfirmSubmit = async () => {
            if (isSubmitting || isSubmitted) return;
            setIsSubmitting(true);
            try {
                const currentStatus = form.getFieldValue('status');
                if (currentStatus && currentStatus !== 'Draft') {
                    Modal.warning({ title: 'Sudah Disubmit', content: 'Transfer In ini sudah disubmit sebelumnya.' });
                    return;
                }

                const payload = {
                    items: dataSourceRef.current,
                    warehouseCodeTo: form.getFieldValue('warehouse_code_to'),
                    transInNo: form.getFieldValue('trans_in_no'),
                    transOutNo: form.getFieldValue('trans_out_no'),
                    transRequestNo: form.getFieldValue('trans_request_no'),
                };

                const submitRes = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/submit-transfer-in`, payload);
                if (submitRes.status === 409) {
                    Modal.warning({ title: 'Sudah Disubmit', content: 'Transfer In ini sudah disubmit sebelumnya.' });
                    return;
                }

                let logOk = true;
                try {
                    const logRes = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/log-spareparts/100/transfer-in`, {
                        trans_in_no: payload.transInNo
                    });
                    if (logRes.status === 409) {
                        // already logged, treat as ok
                    }
                } catch (logErr: any) {
                    if (logErr?.response?.status === 409) {
                        // already logged → treat as ok
                    } else {
                        logOk = false;
                        console.error("Log transfer in error:", logErr);
                    }
                }

                const transNo = payload.transInNo || trans_in_no;
                let newStatus = '';
                if (transNo) {
                    const encodedTransNo = encodeURIComponent(transNo);
                    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wh-transfer-in-data/${encodedTransNo}`);
                    const { header } = response.data;
                    newStatus = header.status;
                    form.setFieldsValue({ status: header.status });
                }

                if (newStatus && newStatus !== 'Draft') {
                    setIsSubmitted(true);
                    setAutosaveStatus('stopped');
                    notification.success({
                        message: 'Sukses',
                        description: logOk
                            ? 'Transfer In telah berhasil disubmit dan stok telah diperbarui.'
                            : 'Transfer In disubmit, namun log spareparts gagal dicatat.',
                    });
                } else {
                    Modal.error({ title: 'Submit Gagal', content: 'Status tidak berubah setelah submit. Coba lagi.' });
                }

            } catch (error: any) {
                if (error?.response?.status === 409) {
                    Modal.warning({ title: 'Sudah Disubmit', content: 'Transfer In ini sudah disubmit sebelumnya.' });
                    setIsSubmitted(true);
                    setAutosaveStatus('stopped');
                } else {
                    const errorMessage = error.response?.data?.error || "Terjadi kesalahan tak terduga saat proses submit.";
                    Modal.error({ title: 'Submit Gagal', content: errorMessage });
                }
            } finally {
                setIsSubmitting(false);
            }
        };

        const handlePrint = async () => {
            const currentTransInNo = form.getFieldValue('trans_in_no');
            if (!currentTransInNo) {
                notification.warning({ message: "Nomor Transfer In tidak ditemukan untuk dicetak." });
                return;
            }
            setIsPrinting(true);
            try {
                const formValues = form.getFieldsValue(true);
                const getWarehouseCity = (code: string) => { 
                    switch (code) { 
                        case "WH_AMP_PBUN": return "Pangkalanbun"; 
                        case "WH_AMP_TWH": return "Teweh"; 
                        case "WH_AMP_PKY": return "Palangkaraya"; 
                        case "WH_AMP_SPT": return "Sampit"; 
                        default: return code || ""; 
                    } 
                }; 
                const reportPayload = {
                    wh_transfer_in: [{
                        trans_in_no: formValues.trans_in_no || "",
                        trans_in_date: formValues.trans_in_date || "",
                        trans_out_no: formValues.trans_out_no || "",
                        trans_out_date: formValues.trans_out_date || "",
                        trans_request_no: formValues.trans_request_no || "",
                        trans_request_date: formValues.trans_request_date || "",
                        warehouse_name_from: formValues.warehouse_name_from || "",
                        warehouse_code_from: formValues.warehouse_code_from || "",
                        warehouse_name_to: formValues.warehouse_name_to || "",
                        warehouse_code_to: formValues.warehouse_code_to || "",
                        warehouse_city_to: getWarehouseCity(formValues.warehouse_code_to || ""),
                        purpose: formValues.purpose || "",
                        approval: formValues.approval || "",
                        approval_date: formValues.approval_date || "",
                        approval_remark: formValues.approval_remark || "",
                        created: formValues.created || "",
                        status: formValues.status || "",
                    }],
                    table_wh_transfer_in: dataSourceRef.current.map((item, index) => ({
                        number_seq: index + 1,
                        item_number: item.item_number || "",
                        item_name: item.item_name || "",
                        uom: item.uom || "",
                        location: item.location || "",
                        qty_available: Number(item.qty_available) || 0,
                        qty_request: Number(item.qty_request) || 0,
                        qty_out: Number(item.qty_out) || 0,
                        qty_in: Number(item.qty_in) || 0,
                    }))
                };
                await import("stimulsoft-reports-js/Css/stimulsoft.viewer.office2013.whiteblue.css");
                await import("stimulsoft-reports-js/Scripts/stimulsoft.reports");
                const viewerModule = await import("stimulsoft-reports-js/Scripts/stimulsoft.viewer");
                const Stimulsoft = viewerModule.Stimulsoft || (window as any).Stimulsoft;
                StartLicense(Stimulsoft);
                setIsReportModalOpen(true);
                setTimeout(() => {
                    const report = Stimulsoft.Report.StiReport.createNewReport();
                    report.loadFile("/reports/wh_transfer_in/wh_transfer_in.mrt");
                    const dataSet = new Stimulsoft.System.Data.DataSet("wh_transfer_in");
                    dataSet.readJson(reportPayload);
                    report.regData(dataSet.dataSetName, "", dataSet);
                    report.dictionary.synchronize();
                    const options = new Stimulsoft.Viewer.StiViewerOptions();
                    options.toolbar.showOpenButton = true;
                    const viewer = new Stimulsoft.Viewer.StiViewer(options, "StiViewer", false);
                    (viewer as any).onOpenReport = (args: any) => {
                        const newReport = args.report;
                        if (newReport) {
                            newReport.regData(dataSet.dataSetName, "", dataSet);
                            newReport.dictionary.synchronize();
                            viewer.report = newReport;
                        }
                    };
                    viewer.report = report;
                    viewer.renderHtml("sti-viewer-container-wh-in");
                }, 300);
            } catch (error) {
                console.error("Gagal mencetak dokumen Transfer In:", error);
                notification.error({ message: "Gagal mencetak Transfer In.", description: "Terjadi kesalahan saat memproses laporan." });
            } finally {
                setIsPrinting(false);
            }
        };

        const handleReportModalCancel = () => {
            setIsReportModalOpen(false);
        };

        const handleDataSelect = async (record: { transferOutNo: string }) => {
            try {
                const checkExisting = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wh-transfer-in-data`);
                const duplicate = checkExisting.data.find((item: any) => item.trans_out_no === record.transferOutNo && Number(item.status) === 1);
                if (duplicate) {
                    Modal.confirm({
                        title: 'Konfirmasi',
                        content: `Nomor ${record.transferOutNo} sudah ada di data dengan status Draft dengan no ${duplicate.trans_in_no}, selesaikan terlebih dahulu`,
                        okText: 'OK',
                        cancelButtonProps: { style: { display: 'none' } }
                    });
                    return;
                }
                const encodedTransferOutNo = encodeURIComponent(record.transferOutNo);
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wh-transfer-out-data/${encodedTransferOutNo}`);
                const { header, items } = response.data;
                const transInNoResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/generate-transfer-in-no`);
                const newTransInNo = transInNoResponse.data.trans_in_no;

                const today = new Date();
                const day = String(today.getDate()).padStart(2, '0');
                const month = String(today.getMonth() + 1).padStart(2, '0');
                const year = today.getFullYear();
                const formattedDate = `${day}/${month}/${year}`;

                const parseDate = (dateStr: string | undefined): string | null => {
                    if (!dateStr || typeof dateStr !== 'string') return null;
                    const parts = dateStr.split('/');
                    if (parts.length === 3) {
                        return `${parts[2]}-${parts[1]}-${parts[0]}`;
                    }
                    return null;
                };

                const headerDataForSave = {
                    created: userData?.kode_grup1,
                    status: 1,
                    trans_in_no: newTransInNo,
                    trans_in_date: parseDate(formattedDate),
                    trans_out_no: header.trans_out_no,
                    trans_out_date: parseDate(header.trans_out_date),
                    trans_request_no: header.trans_request_no,
                    trans_request_date: parseDate(header.trans_request_date),
                    warehouse_name_from: header.warehouse_name_req,
                    warehouse_code_from: header.warehouse_code_req,
                    warehouse_name_to: header.warehouse_name_to,
                    warehouse_code_to: header.warehouse_code_to,
                    purpose: header.purpose,
                    approval: header.approval_by,
                    approval_date: parseDate(header.approval_date),
                    approval_remark: header.approval_remark,
                };

                const itemsDataForSave = items.map((item: any) => ({
                    trans_in_no: newTransInNo,
                    item_number: item.item_number,
                    item_name: item.item_name,
                    uom: item.uom,
                    location: item.location || "",
                    qty_available: item.qty_available,
                    qty_request: item.qty_request,
                    qty_out: item.qty_out,
                    qty_in: "",
                }));

                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/save-transfer-in`, {
                    header: headerDataForSave,
                    items: itemsDataForSave,
                });

                const hasMissingLocation = items.some((item: any) => !item.location);
                if (hasMissingLocation) {
                    Modal.confirm({
                        title: 'Konfirmasi',
                        content: 'Beberapa Item tidak memiliki lokasi rak, silahkan lengkapi untuk proses selanjutnya',
                        okText: 'OK',
                        cancelButtonProps: { style: { display: 'none' } }
                    });
                }

                form.setFieldsValue({
                    trans_in_no: newTransInNo,
                    trans_out_no: header.trans_out_no,
                    trans_out_date: header.trans_out_date,
                    trans_request_no: header.trans_request_no,
                    trans_request_date: header.trans_request_date,
                    warehouse_name_from: header.warehouse_name_req,
                    warehouse_code_from: header.warehouse_code_req,
                    warehouse_name_to: header.warehouse_name_to,
                    warehouse_code_to: header.warehouse_code_to,
                    purpose: header.purpose,
                    approval: header.approval_by,
                    approval_date: header.approval_date,
                    approval_remark: header.approval_remark,
                });

                const newDataSource = items.map((item: any, index: number) => ({
                    key: `${item.item_number}-${index}`,
                    item_number: item.item_number,
                    item_name: item.item_name,
                    uom: item.uom,
                    qty_available: item.qty_available,
                    qty_request: item.qty_request,
                    qty_out: item.qty_out,
                    qty_in: "",
                    location: item.location || "",
                }));
                setDataSource(newDataSource);
                setIsModalOpen(false);
                setAutosaveStatus('idle');

            } catch (error) {
                setAutosaveStatus('error');
                console.error("Gagal mengambil detail, generate nomor, atau menyimpan data awal:", error);
            }
        };

        useEffect(() => {
            const loadTransferInData = async (transNo: string) => {
                try {
                    const encodedTransNo = encodeURIComponent(transNo);
                    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wh-transfer-in-data/${encodedTransNo}`);
                    const { header, items } = response.data;

                    form.setFieldsValue({
                        created: header.created_by_name,
                        status: header.status,
                        trans_in_no: header.trans_in_no,
                        trans_in_date: header.trans_in_date,
                        trans_out_no: header.trans_out_no,
                        trans_out_date: header.trans_out_date,
                        trans_request_no: header.trans_request_no,
                        trans_request_date: header.trans_request_date,
                        warehouse_name_from: header.warehouse_name_from,
                        warehouse_code_from: header.warehouse_code_from,
                        warehouse_name_to: header.warehouse_name_to,
                        warehouse_code_to: header.warehouse_code_to,
                        purpose: header.purpose,
                        approval: header.approval,
                        approval_date: header.approval_date,
                        approval_remark: header.approval_remark,
                    });
                    setCreatorCode(header.created);

                    const newDataSource = items.map((item: any, index: number) => ({
                        key: `${item.item_number}-${index}`,
                        item_number: item.item_number,
                        item_name: item.item_name,
                        uom: item.uom,
                        qty_available: item.qty_available,
                        qty_request: item.qty_request,
                        qty_out: item.qty_out,
                        qty_in: item.qty_in || "",
                        location: item.location || "",
                    }));

                    setDataSource(newDataSource);
                    setAutosaveStatus('idle');
                    if (header.status !== 'Draft') {
                        setAutosaveStatus('stopped');
                    } else {
                        setAutosaveStatus('idle');
                    }
                } catch (error) {
                    console.error("Failed to load Transfer In data:", error);
                    setAutosaveStatus('error');
                }
            };

            if (trans_in_no) {
                loadTransferInData(trans_in_no);
            }
        }, [trans_in_no, form]);

        useEffect(() => {
            if (trans_in_no) return;
            if (userData && userData.nama) {
                form.setFieldsValue({ created: userData.nama });
            }

            const today = new Date();
            const day = String(today.getDate()).padStart(2, '0');
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const year = today.getFullYear();
            const formattedDate = `${day}/${month}/${year}`;

            setTransferInDate(formattedDate);
            form.setFieldsValue({ trans_in_date: formattedDate });
            form.setFieldsValue({ status: "Draft" });
        }, [form, userData, trans_in_no]);

        const handleSearch = (
            selectedKeys: string[],
            confirm: FilterDropdownProps['confirm'],
            dataIndex: DataIndex,
        ) => {
            confirm();
            setSearchText(selectedKeys[0]);
            setSearchedColumn(dataIndex);
        };

        const handleReset = (clearFilters: () => void) => {
            clearFilters();
            setSearchText('');
        };

        const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        ref={searchInput}
                        placeholder={`Search ${dataIndex}`}
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => clearFilters && handleReset(clearFilters)}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => {
                                close();
                            }}
                        >
                            close
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
            ),
            onFilter: (value, record) =>
                record[dataIndex]
                    .toString()
                    .toLowerCase()
                    .includes((value as string).toLowerCase()),
            onFilterDropdownOpenChange: (open) => {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
            render: (text) =>
                searchedColumn === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                ) : (
                    text
                ),
        });

        const columns: TableColumnsType<DataType> = [
            {
                title: "Item Number",
                dataIndex: "item_number",
                key: "item_number",
                sorter: (a, b) => a.item_number.localeCompare(b.item_number),
                ...getColumnSearchProps('item_number'),
                onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
                onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            },
            {
                title: "Item Name",
                dataIndex: "item_name",
                key: "item_name",
                sorter: (a, b) => a.item_name.localeCompare(b.item_name),
                ...getColumnSearchProps('item_name'),
                onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
                onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            },
            {
                title: "UoM",
                dataIndex: "uom",
                key: "uom",
                sorter: (a, b) => a.uom.localeCompare(b.uom),
                ...getColumnSearchProps('uom'),
                onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
                onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            },
            {
                title: "Location",
                dataIndex: "location",
                key: "location",
                sorter: (a, b) => a.location.localeCompare(b.location),
                ...getColumnSearchProps('location'),
                onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
                onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            },
            {
                title: "Available Qty",
                dataIndex: "qty_available",
                key: "qty_available",
                sorter: (a, b) => a.qty_available - b.qty_available,
                onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
                onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            },
            {
                title: "Request Qty",
                dataIndex: "qty_request",
                key: "qty_request",
                sorter: (a, b) => a.qty_request - b.qty_request,
                onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
                onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            },
            {
                title: "Out Qty",
                dataIndex: "qty_out",
                key: "qty_out",
                sorter: (a, b) => a.qty_out - b.qty_out,
                onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
                onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            },
            {
                title: "In Qty",
                dataIndex: "qty_in",
                key: "qty_in",
                sorter: (a, b) => Number(a.qty_in) - Number(b.qty_in),
                ...getColumnSearchProps('qty_in'),
                onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
                onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            },
        ];

        const rowSelection: TableProps<DataType>["rowSelection"] = {
            selectedRowKeys,
            onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
        };

        const logicProps: WithItemWHTransferInLogicProps = {
            columns,
            rowSelection,
            transferInDate,
            form,
            isModalOpen,
            showModal,
            handleOk,
            handleCancel,
            dataSource,
            handleDataSelect,
            isEditModalOpen,
            editingRecord,
            handleRowDoubleClick,
            handleEditOk,
            handleEditCancel,
            autosaveStatus,
            onValuesChange: triggerAutosave,
            handleSubmit,
            isSubmitting,
            isSubmitted,
            handlePrint,
            isPrinting,
            isReportModalOpen,
            handleReportModalCancel
        };

        return <WrappedComponent {...props} {...logicProps} />;
    };

    return EnhancedComponent;
};

export default withItemWHTransferInLogic;